import * as Sentry from "@sentry/nextjs";
import axiosInstance from "src/axios";
import type { RestorablePicks } from "./pending-picks";

/**
 * Tell the server which uploads an earlier, dead page load left unfinished.
 *
 * That load presigned its picks, so the server holds an upload intent per
 * key and waits for the bytes. When the customer resumes the offer, the
 * pictures are uploaded again under NEW keys; when they discard it, they are
 * not uploaded at all. Either way the old intents would wait forever, and Pay
 * answered UPLOADS_PENDING ("some photos you started uploading earlier didn't
 * arrive") for a cart that was complete (order dc6a764b: 51 of them).
 *
 * The server gives up only on this order's waiting intents under these keys
 * that storage confirms missing; anything that did land is registered by its
 * sweep as usual. Called only once the earlier load is known to be gone (the
 * offer itself is gated on its Web Lock being released).
 *
 * Best effort: never throws, never holds the customer longer than
 * `timeoutMs`, and reports a failure to Sentry once per page.
 */

/** Keys per request; the backend refuses more. */
export const ABANDON_KEYS_PER_REQUEST = 2000;

/** The longest the resume/discard handler waits for the answer. */
export const ABANDON_TIMEOUT_MS = 5000;

let reported = false;

/** The order a pick session belongs to, or null for "new" (no order yet). */
export function orderIdOfPickSession(session: string): string | null {
  return session.startsWith("order:") ? session.slice("order:".length) || null : null;
}

type Post = (
  url: string,
  body: { keys: string[] },
  config: { headers?: Record<string, string>; timeout: number },
) => Promise<unknown>;

export async function abandonLeftBehindUploads(opts: {
  orderId: string | null | undefined;
  guestToken?: string | null;
  keys: ReadonlyArray<string>;
  timeoutMs?: number;
  post?: Post;
}): Promise<void> {
  const keys = [...new Set(opts.keys.filter(Boolean))];
  if (!opts.orderId || keys.length === 0) return;
  const timeoutMs = opts.timeoutMs ?? ABANDON_TIMEOUT_MS;
  const post: Post =
    opts.post ?? ((url, body, config) => axiosInstance.post(url, body, config));
  const headers = opts.guestToken ? { "x-guest-token": opts.guestToken } : undefined;
  const url = `/order/${encodeURIComponent(opts.orderId)}/upload-intents/abandon`;

  let send: Promise<void>;
  try {
    send = (async () => {
      for (let i = 0; i < keys.length; i += ABANDON_KEYS_PER_REQUEST) {
        await post(
          url,
          { keys: keys.slice(i, i + ABANDON_KEYS_PER_REQUEST) },
          { headers, timeout: timeoutMs },
        );
      }
    })();
  } catch (error) {
    send = Promise.reject(error);
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), timeoutMs);
  });
  try {
    await Promise.race([send, timedOut]);
  } catch (error) {
    if (!reported) {
      reported = true;
      Sentry.captureException(error, {
        level: "warning",
        tags: { area: "photo-upload", stage: "abandon-left-behind-uploads" },
        extra: { orderId: opts.orderId, keys: keys.length },
      });
    }
  } finally {
    if (timer) clearTimeout(timer);
    // A request still running past the timeout must not surface later as an
    // unhandled rejection.
    send.catch(() => undefined);
  }
}

/**
 * For a resumed or discarded reload offer: abandon what that earlier load
 * left behind on the order the offer belongs to. Skipped for an offer with
 * no order yet (no intents can exist) or no recorded keys.
 */
export function abandonForOffer(
  offer: Pick<RestorablePicks, "session" | "keys">,
  guestTokenFor: (orderId: string) => string | null | undefined,
  abandon: typeof abandonLeftBehindUploads = abandonLeftBehindUploads,
): Promise<void> {
  const orderId = orderIdOfPickSession(offer.session);
  if (!orderId || !offer.keys?.length) return Promise.resolve();
  let guestToken: string | null | undefined;
  try {
    guestToken = guestTokenFor(orderId);
  } catch {
    guestToken = null;
  }
  return abandon({ orderId, guestToken, keys: offer.keys });
}

/** Test seam: allow the next failure to be reported again. */
export function __resetAbandonReportForTest(): void {
  reported = false;
}
